import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const API_URL = process.env.REACT_APP_API_URL

export default function Profile({ user, staffRecord, role, staffId }) {
  const [profile, setProfile] = useState(null)
  const [staff, setStaff] = useState(staffRecord)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [bio, setBio] = useState('')
  const [stripeStatus, setStripeStatus] = useState(null)
  const [connectingStripe, setConnectingStripe] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordMsg, setPasswordMsg] = useState(null)
  const [calendarCopied, setCalendarCopied] = useState(false)

  useEffect(() => {
    fetchProfile()
  }, [])

  async function fetchProfile() {
    const { data: profileData } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()

    if (profileData) {
      setProfile(profileData)
      setName(profileData.full_name || '')
      setPhone(profileData.phone || '')
    }

    if (staffRecord) {
      setBio(staffRecord.bio || '')
      setStaff(staffRecord)
      if (staffRecord.stripe_account_id) {
        checkStripeStatus(staffRecord.stripe_account_id)
      }
    }

    setLoading(false)
  }

  async function checkStripeStatus(accountId) {
    try {
      const response = await fetch(`${API_URL}/connect/account-status/${accountId}`)
      const data = await response.json()
      setStripeStatus(data)
    } catch (error) {
      console.log('Could not check Stripe status')
    }
  }

  async function saveProfile() {
    setSaving(true)

    await supabase
      .from('profiles')
      .update({ full_name: name, phone })
      .eq('id', user.id)

    if (staff) {
      await supabase
        .from('staff')
        .update({ full_name: name, bio })
        .eq('id', staff.id)
    }

    setSaving(false)
    setEditing(false)
    fetchProfile()
  }

  async function startStripeOnboarding() {
    setConnectingStripe(true)
    try {
      let accountId = staff?.stripe_account_id

      if (!accountId) {
        const response = await fetch(`${API_URL}/connect/create-account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            staffId: staff?.id,
            email: user.email,
            name: name || user.email,
          }),
        })
        const data = await response.json()
        accountId = data.accountId
      }

      const linkResponse = await fetch(`${API_URL}/connect/onboarding-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId, staffId: staff?.id }),
      })
      const linkData = await linkResponse.json()
      window.open(linkData.url, '_blank')

      setTimeout(() => {
        if (staff?.stripe_account_id) checkStripeStatus(staff.stripe_account_id)
      }, 5000)
    } catch (error) {
      alert('Could not start Stripe onboarding. Please try again.')
    }
    setConnectingStripe(false)
  }

  async function changePassword() {
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Passwords do not match.' })
      return
    }
    if (newPassword.length < 6) {
      setPasswordMsg({ type: 'error', text: 'Password must be at least 6 characters.' })
      return
    }

    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) {
      setPasswordMsg({ type: 'error', text: error.message })
    } else {
      setPasswordMsg({ type: 'success', text: 'Password updated successfully!' })
      setNewPassword('')
      setConfirmPassword('')
      setChangingPassword(false)
    }
  }

  function copyCalendarUrl() {
    const url = `${API_URL}/api/ical/provider/${staffId}`
    navigator.clipboard.writeText(url).then(() => {
      setCalendarCopied(true)
      setTimeout(() => setCalendarCopied(false), 3000)
    })
  }

  function getInitials(name) {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  if (loading) return <div style={styles.empty}>Loading...</div>

  const isStripeConnected = staff?.stripe_onboarding_complete || stripeStatus?.chargesEnabled
  const calendarUrl = `${API_URL}/api/ical/provider/${staffId}`

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>Profile</h1>

      <div style={styles.layout}>
        {/* Left column */}
        <div style={styles.leftColumn}>

          {/* Avatar & name */}
          <div style={styles.avatarCard}>
            <div style={styles.avatar}>
              {getInitials(profile?.full_name)}
            </div>
            <div style={styles.avatarName}>{profile?.full_name}</div>
            <div style={styles.avatarEmail}>{user.email}</div>
            <div style={styles.roleBadge}>{role?.toUpperCase()}</div>
          </div>

          {/* Stripe Connect */}
          {staff && (
            <div style={styles.card}>
              <div style={styles.cardTitle}>PAYMENT SETUP</div>
              <p style={styles.cardDesc}>
                Connect your bank account to receive client payments directly.
              </p>

              <div style={styles.stripeStatus}>
                <div style={styles.stripeStatusLabel}>Status</div>
                <div style={{
                  ...styles.stripeStatusBadge,
                  backgroundColor: isStripeConnected ? 'rgba(74,117,74,0.15)' : 'rgba(245,158,11,0.15)',
                  color: isStripeConnected ? '#4A754A' : '#f59e0b',
                  border: `1px solid ${isStripeConnected ? 'rgba(74,117,74,0.3)' : 'rgba(245,158,11,0.3)'}`,
                }}>
                  {isStripeConnected ? '✓ Connected' : staff?.stripe_account_id ? 'In Progress' : 'Not Connected'}
                </div>
              </div>

              {!isStripeConnected && (
                <button
                  style={styles.stripeBtn}
                  onClick={startStripeOnboarding}
                  disabled={connectingStripe}>
                  {connectingStripe ? 'Loading...' : staff?.stripe_account_id ? 'Continue Setup' : 'Connect Bank Account'}
                </button>
              )}

              {isStripeConnected && (
                <div style={styles.stripeConnected}>
                  🎉 Payments will be deposited directly to your bank account.
                </div>
              )}

              {staff?.stripe_account_id && !isStripeConnected && (
                <button
                  style={styles.checkStatusBtn}
                  onClick={() => checkStripeStatus(staff.stripe_account_id)}>
                  Check Status
                </button>
              )}
            </div>
          )}

          {/* Calendar Sync */}
          {staff && staffId && (
            <div style={styles.card}>
              <div style={styles.cardTitle}>CALENDAR SYNC</div>
              <p style={styles.cardDesc}>
                Subscribe once — new appointments appear on your calendar automatically in real time.
              </p>

              <div style={styles.calendarUrlBox}>
                {calendarUrl}
              </div>

              <button
                style={{
                  ...styles.stripeBtn,
                  backgroundColor: calendarCopied ? '#4A754A' : '#D4AF77',
                }}
                onClick={copyCalendarUrl}>
                {calendarCopied ? '✓ Copied!' : 'COPY CALENDAR URL'}
              </button>

              <div style={styles.calendarInstructions}>
                <p style={styles.calendarInstructionTitle}>📱 iPhone / Apple Calendar:</p>
                <p style={styles.calendarInstructionText}>
                  Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar → Paste URL
                </p>
                <p style={styles.calendarInstructionTitle}>📅 Google Calendar:</p>
                <p style={styles.calendarInstructionText}>
                  Other Calendars → + → From URL → Paste URL
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right column */}
        <div style={styles.rightColumn}>

          {/* Personal info */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitle}>PERSONAL INFO</div>
              <button
                style={styles.editLink}
                onClick={() => setEditing(!editing)}>
                {editing ? 'Cancel' : 'Edit'}
              </button>
            </div>

            <div style={styles.fieldRow}>
              <div style={styles.fieldLabel}>Full Name</div>
              {editing ? (
                <input
                  style={styles.input}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your full name"
                />
              ) : (
                <div style={styles.fieldValue}>{profile?.full_name || '—'}</div>
              )}
            </div>

            <div style={styles.fieldRow}>
              <div style={styles.fieldLabel}>Email</div>
              <div style={styles.fieldValue}>{user.email}</div>
            </div>

            <div style={styles.fieldRow}>
              <div style={styles.fieldLabel}>Phone</div>
              {editing ? (
                <input
                  style={styles.input}
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="Your phone number"
                />
              ) : (
                <div style={styles.fieldValue}>{profile?.phone || '—'}</div>
              )}
            </div>

            {staff && (
              <div style={styles.fieldRow}>
                <div style={styles.fieldLabel}>Bio</div>
                {editing ? (
                  <textarea
                    style={styles.textarea}
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    rows={3}
                    placeholder="Short description shown to clients..."
                  />
                ) : (
                  <div style={styles.fieldValue}>{staff?.bio || '—'}</div>
                )}
              </div>
            )}

            {editing && (
              <button
                style={styles.saveBtn}
                onClick={saveProfile}
                disabled={saving}>
                {saving ? 'Saving...' : 'SAVE CHANGES'}
              </button>
            )}
          </div>

          {/* Specialties */}
          {staff?.specialties?.length > 0 && (
            <div style={styles.card}>
              <div style={styles.cardTitle}>SPECIALTIES</div>
              <div style={styles.specialties}>
                {staff.specialties.map(s => (
                  <span key={s} style={styles.specialtyTag}>{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Password */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitle}>PASSWORD</div>
              <button
                style={styles.editLink}
                onClick={() => setChangingPassword(!changingPassword)}>
                {changingPassword ? 'Cancel' : 'Change'}
              </button>
            </div>

            {changingPassword ? (
              <div>
                <label style={styles.label}>New Password</label>
                <input
                  style={styles.input}
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                />
                <label style={styles.label}>Confirm Password</label>
                <input
                  style={styles.input}
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                />
                {passwordMsg && (
                  <p style={{
                    fontSize: '13px',
                    color: passwordMsg.type === 'error' ? '#ef4444' : '#4A754A',
                    marginTop: '8px',
                  }}>
                    {passwordMsg.text}
                  </p>
                )}
                <button style={styles.saveBtn} onClick={changePassword}>
                  UPDATE PASSWORD
                </button>
              </div>
            ) : (
              <div style={styles.fieldValue}>••••••••</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const styles = {
  container: { padding: '32px', maxWidth: '1100px' },
  title: {
    fontSize: '28px', fontWeight: '600', color: '#D4AF77',
    letterSpacing: '2px', textTransform: 'uppercase',
    fontFamily: 'Cormorant Garamond, serif', marginBottom: '24px',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: '24px',
    alignItems: 'start',
  },
  leftColumn: { display: 'flex', flexDirection: 'column', gap: '16px' },
  rightColumn: { display: 'flex', flexDirection: 'column', gap: '16px' },
  avatarCard: {
    backgroundColor: '#111', border: '1px solid #1a1a1a',
    borderRadius: '2px', padding: '24px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
  },
  avatar: {
    width: '72px', height: '72px', borderRadius: '50%',
    backgroundColor: '#D4AF77', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '24px', fontWeight: '700',
    color: '#000', marginBottom: '8px',
  },
  avatarName: { fontSize: '16px', fontWeight: '600', color: '#F5F0E8' },
  avatarEmail: { fontSize: '12px', color: '#555' },
  roleBadge: {
    fontSize: '9px', color: '#D4AF77', letterSpacing: '2px',
    border: '1px solid rgba(212,175,119,0.3)', padding: '3px 10px',
    borderRadius: '2px', marginTop: '4px',
  },
  card: {
    backgroundColor: '#111', border: '1px solid #1a1a1a',
    borderRadius: '2px', padding: '20px',
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '10px', fontWeight: '600', color: '#555',
    letterSpacing: '2px', marginBottom: '8px',
  },
  cardDesc: {
    fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '16px',
  },
  editLink: {
    background: 'none', border: 'none', color: '#D4AF77',
    fontSize: '12px', cursor: 'pointer', letterSpacing: '0.5px',
    fontFamily: 'Montserrat, sans-serif',
  },
  fieldRow: {
    borderBottom: '1px solid #1a1a1a',
    paddingBottom: '12px', marginBottom: '12px',
  },
  fieldLabel: {
    fontSize: '10px', color: '#555', letterSpacing: '1.5px',
    textTransform: 'uppercase', marginBottom: '4px',
  },
  fieldValue: { fontSize: '14px', color: '#F5F0E8' },
  input: {
    width: '100%', padding: '10px', backgroundColor: '#0a0a0a',
    border: '1px solid #222', borderRadius: '2px', color: '#F5F0E8',
    fontSize: '14px', fontFamily: 'Montserrat, sans-serif',
    outline: 'none', boxSizing: 'border-box', marginTop: '4px',
  },
  textarea: {
    width: '100%', padding: '10px', backgroundColor: '#0a0a0a',
    border: '1px solid #222', borderRadius: '2px', color: '#F5F0E8',
    fontSize: '14px', fontFamily: 'Montserrat, sans-serif',
    outline: 'none', resize: 'vertical', boxSizing: 'border-box', marginTop: '4px',
  },
  label: {
    display: 'block', fontSize: '10px', fontWeight: '600', color: '#555',
    letterSpacing: '2px', textTransform: 'uppercase',
    marginBottom: '6px', marginTop: '12px',
    fontFamily: 'Montserrat, sans-serif',
  },
  saveBtn: {
    marginTop: '16px', padding: '12px 24px', backgroundColor: '#D4AF77',
    color: '#000', border: 'none', borderRadius: '2px', fontSize: '11px',
    fontWeight: '700', letterSpacing: '2px', cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif', width: '100%',
  },
  stripeStatus: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '16px',
  },
  stripeStatusLabel: { fontSize: '13px', color: '#555' },
  stripeStatusBadge: {
    fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px',
    padding: '4px 12px', borderRadius: '2px',
  },
  stripeBtn: {
    width: '100%', padding: '12px', backgroundColor: '#D4AF77',
    color: '#000', border: 'none', borderRadius: '2px', fontSize: '11px',
    fontWeight: '700', letterSpacing: '2px', cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif', marginBottom: '8px',
    transition: 'background-color 0.2s',
  },
  checkStatusBtn: {
    width: '100%', padding: '12px', backgroundColor: 'transparent',
    color: '#555', border: '1px solid #222', borderRadius: '2px',
    fontSize: '11px', fontWeight: '600', letterSpacing: '1.5px',
    cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
  },
  stripeConnected: {
    fontSize: '13px', color: '#4A754A', lineHeight: '1.6', marginTop: '8px',
  },
  calendarUrlBox: {
    backgroundColor: '#0a0a0a', border: '1px solid #222',
    borderRadius: '2px', padding: '10px 12px', marginBottom: '12px',
    fontSize: '10px', color: '#555', wordBreak: 'break-all',
    letterSpacing: '0.3px', lineHeight: '1.6',
  },
  calendarInstructions: {
    marginTop: '12px',
    borderTop: '1px solid #1a1a1a',
    paddingTop: '12px',
  },
  calendarInstructionTitle: {
    fontSize: '11px', fontWeight: '600', color: '#888',
    marginBottom: '4px', marginTop: '10px',
  },
  calendarInstructionText: {
    fontSize: '11px', color: '#444', lineHeight: '1.6',
  },
  specialties: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  specialtyTag: {
    fontSize: '11px', color: '#D4AF77',
    backgroundColor: 'rgba(212,175,119,0.1)',
    border: '1px solid rgba(212,175,119,0.2)',
    padding: '4px 12px', borderRadius: '2px',
  },
  empty: { padding: '32px', color: '#555' },
}